import React from 'react';
import styles from './MiDebugPanel.module.css';

const labels = {
    schema_validation: '各层 Schema 校验', contract_version: '契约版本', status: '校验状态',
    attempts: '调用次数', model_valid: '模型输出符合契约', missing_fields: '模型漏填字段',
    invalid_fields: '格式错误字段', normalized_valid: '规范化后符合契约', normalized_issues: '规范化后问题',
    model_needs_mi: '模型原始 MI 判断', effective_needs_mi: '最终 MI 判断', needs_mi_source: 'MI 判断来源',
    retry_issues: '重试原因', normalized_changed_fields: '程序调整字段',
    turn: '轮次判断', plan: '策略计划', draft: '回复草稿', citations: '引用', gate: '安全闸门',
    session: '累计会话状态', discussed_topics: '已讨论主题', unresolved_concerns: '未解决顾虑',
    change_talk_log: '改变语句记录', sustain_talk_log: '维持语句记录',
    recent_strategy_history: '最近策略', rolling_summary: '会话摘要', current_state: '当前状态',
    plan_version: '策略版本', primary_strategy: '主要策略', supporting_moves: '辅助动作',
    reflection_target: '反映内容', information_action: '信息动作', question: '提问设置',
    allowed: '允许提问', type: '类型', goal: '目的', planning_allowed: '允许制定计划',
    autonomy_support_needed: '需要支持自主性', avoid: '应避免', strategy_reason_codes: '策略原因',
    schema_version: '结构版本', turn_id: '轮次 ID', route: '处理路由', route_reason_codes: '路由原因',
    matched_qa_id: '匹配问答 ID', intent_topic: '意图主题', current_focus: '当前关注点',
    focus_candidates: '候选关注点', mi_process: 'MI 阶段', client_speech_type: '用户言语类型',
    darn_cat_primary: '主要改变言语类型', change_talk_excerpt: '改变言语摘录', sustain_talk_excerpt: '维持言语摘录',
    sustain_strength: '维持言语强度', emotion: '情绪', autonomy_threat: '自主性威胁',
    confidence_domain: '信心领域', confidence_direction: '信心方向', needs_mi: '需要 MI',
    direct_answer_required: '需要直接回答', is_composite: '复合问题', planning_readiness: '计划准备度',
    information_need: '信息需求', multiple_unresolved_concerns: '存在多个未解决顾虑',
    previous_strategy_repeated: '重复上一策略', response_to_previous_strategy: '对上一策略的反应',
    relationship_rupture: '关系破裂', retrieval: '检索', needs_medical_rag: '需要医学检索',
    medical_query: '医学检索词', medical_filters: '医学筛选条件', product_scope: '产品范围',
    population: '适用人群', topic: '主题', jurisdiction: '辖区', authority_preference: '权威来源偏好',
    needs_service_lookup: '需要服务查询', service_query: '服务查询词', state_delta: '状态变化',
    discussed_topics_add: '新增已讨论主题', unresolved_concerns_add: '新增未解决顾虑',
    unresolved_concerns_resolve: '已解决顾虑', confidence: '判断置信度', response_version: '回复版本',
    response_text: '回复内容', citation_ids: '引用 ID', service_record_ids: '服务记录 ID',
    strategy_used: '采用策略', focus_addressed: '回应关注点', question_count: '提问数量',
    state_update_hint: '状态更新提示', focus_resolved: '关注点已解决', new_unresolved_concern: '新增未解决顾虑',
    gate_version: '闸门版本', decision: '审核结果', risk_tags: '风险标签', final_response_text: '最终回复',
    final_citation_ids: '最终引用 ID', final_service_record_ids: '最终服务记录 ID',
    repair_actions: '修正动作', audit_summary: '审核摘要', post_hoc: '事后审核'
};

const values = {
    model_valid: '模型输出完整', program_normalized: '程序已规范化', fallback: '回退处理',
    model: '模型', program: '程序', skipped: '跳过', rules: '规则校验',
    none: '无', unknown: '未知', not_applicable: '不适用', not_available: '暂无', neutral: '中性',
    pass: '通过', insufficient_scope: '超出服务范围', out_of_scope_handoff: '范围外转介',
    out_of_scope_redirect: '范围外引导', strip_internal_citations: '移除内部引用'
};

function formatValue(value) {
    if (value == null) return '未提供';
    if (value === true) return '是';
    if (value === false) return '否';
    if (value === '') return '空文本';
    if (Array.isArray(value)) return value.length ? value.map(formatValue).join('、') : '无（空列表）';
    return values[value] ? `${values[value]}（${value}）` : String(value);
}

// 展开嵌套对象和对象列表；未知字段同样展示，兼容后端扩展。
function rowsFor(value, path = [], titles = []) {
    if (value && typeof value === 'object') {
        if (Array.isArray(value) && value.every(item => item == null || typeof item !== 'object')) {
            return [{path, titles, raw: value, value: formatValue(value)}];
        }
        const entries = Object.entries(value);
        if (!entries.length) return [{path, titles, raw: value, value: '无（空对象）'}];
        return entries.flatMap(([key, child]) => rowsFor(
            child, [...path, key], [...titles, Array.isArray(value) ? `第 ${Number(key) + 1} 项` : labels[key] || key]
        ));
    }
    return [{path, titles, raw: value, value: formatValue(value)}];
}

export function previousMiStates(messages) {
    let previous;
    return messages.map(message => {
        const result = previous;
        if (message.miState) previous = message.miState;
        return result;
    });
}

export default function MiDebugPanel({data, previousData, enabled}) {
    if (!enabled || !data || typeof data !== 'object') return null;
    return (
        <details className={styles.panel} open>
            <summary>MI 调试信息{data.turn?.turn_id != null ? ` · 第 ${data.turn.turn_id} 轮` : ''}</summary>
            <p className={styles.legend}>{previousData ? '加粗项表示相较上一轮发生变化；悬停可查看上一轮的值。' : '首轮记录，暂无上一轮可比较。'}</p>
            <div className={styles.sections}>
                {Array.from(new Set([...Object.keys(data), ...Object.keys(previousData || {})])).map(section => {
                    const currentRows = Object.prototype.hasOwnProperty.call(data, section) ? rowsFor(data[section]) : [];
                    const oldRows = previousData && Object.prototype.hasOwnProperty.call(previousData, section) ? rowsFor(previousData[section]) : [];
                    const oldByPath = new Map(oldRows.map(row => [JSON.stringify(row.path), row]));
                    const currentPaths = new Set(currentRows.map(row => JSON.stringify(row.path)));
                    const rows = [...currentRows, ...oldRows.filter(row =>
                        !currentPaths.has(JSON.stringify(row.path)) &&
                        !currentRows.some(current => row.path.every((part, index) => current.path[index] === part))
                    ).map(row => ({...row, removed: true, value: '本轮未提供'}))];
                    return (
                    <div className={styles.section} key={section}>
                        <table role="table">
                            <caption>{labels[section] || section}</caption>
                            <thead><tr><th scope="col">字段</th><th scope="col">值</th></tr></thead>
                            <tbody className={styles.fieldGrid}>
                                {rows.map(row => {
                                    const old = oldByPath.get(JSON.stringify(row.path));
                                    const changed = Boolean(previousData) && (row.removed || !old || JSON.stringify(old.raw) !== JSON.stringify(row.raw));
                                    return (
                                    <tr key={row.path.join('.') || section} className={changed ? styles.changed : undefined}
                                        data-changed={changed || undefined} title={changed ? `上一轮：${old ? old.value : '未提供'}` : undefined}>
                                        <th scope="row" role="rowheader">
                                            {row.titles.join(' / ') || labels[section] || section}
                                            <small>{[section, ...row.path].join('.')}</small>
                                        </th>
                                        <td role="cell">{changed ? <strong>{row.value}</strong> : row.value}</td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    );
                })}
            </div>
        </details>
    );
}
